/**
 * Cotización x mayor en hoja carta — mismo estilo que la factura x mayor
 * (impresionxmayor.js) pero como documento informativo: título COTIZACIÓN,
 * "Válida hasta" destacada, líneas con cantidad e importe, plan de cuotas
 * simulado y la nota legal de no compromiso de las cotizaciones.
 */
import { imprimirHtml } from "lib/impresionhtml";

const NAVY = "#18586b";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const esc = (s) =>
  String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch]));

const logoImg = (comp) =>
  comp && typeof comp.logo === "string" && comp.logo.startsWith("data:image/")
    ? `<img class="logo" src="${comp.logo}" alt="" />`
    : "";

export const monedaLabel = (m) => (m === "U" ? "USD" : "RD$");

/**
 * @param {Object} det            Respuesta de /api/cotizacionxmayor_detalle o _crear.
 * @param {Object} det.cotizacion Cabecera de cotizaciones_xmayor.
 * @param {Array}  det.items      Detalle [{chasis, descripcion, cantidad, preciounitario, importe}].
 * @param {Array}  det.cuotas     Plan simulado [{numero, fecha, valor}] (solo crédito).
 * @param {Object} det.compania   Membrete {nombre, rnc, direccion, telefono, logo}.
 */
export function imprimirCotizacionXmayor(det) {
  const q = det?.cotizacion || {};
  const c = det?.compania || {};
  const items = det?.items || [];
  const cuotas = det?.cuotas || [];
  const mon = monedaLabel(q.moneda);

  const filasLineas = items
    .map(
      (l, i) => `<tr>
          <td>${i + 1}</td>
          <td class="num">${esc(l.cantidad ?? 1)}</td>
          <td>${esc(l.descripcion)}</td>
          <td>${esc(l.chasis || "—")}</td>
          <td class="num">${fmtMoney(l.preciounitario)}</td>
          <td class="num">${fmtMoney(l.importe)}</td>
        </tr>`
    )
    .join("");

  const filasCuotas = cuotas
    .map(
      (cu) => `<tr>
          <td>${esc(cu.numero)}</td>
          <td>${esc(cu.fecha)}</td>
          <td class="num">${fmtMoney(cu.valor)}</td>
        </tr>`
    )
    .join("");

  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Cotización x Mayor</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 60px; max-width: 170px; object-fit: contain; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .doc .vence { color: ${NAVY}; font-weight: bold; }
      .cliente { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                 display: flex; gap: 30px; flex-wrap: wrap; }
      .cliente span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .cliente b { font-size: 12px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      table.lineas tr:nth-child(even) td { background: #f5f8fa; }
      th.num, td.num { text-align: right; }
      .total { margin: 12px 0; text-align: right; }
      .total .monto { display: inline-block; background: #f0f5f8; border: 1px solid #d7dee5; border-radius: 6px;
                      padding: 8px 16px; font-size: 14px; font-weight: bold; color: ${NAVY}; }
      .cuotas-titulo { margin: 16px 0 4px; font-weight: bold; color: ${NAVY}; font-size: 12px; text-transform: uppercase; }
      table.cuotas { width: 55%; border-collapse: collapse; }
      table.cuotas th { background: #f0f5f8; color: ${NAVY}; padding: 5px 8px; font-size: 11px; text-align: left; }
      table.cuotas td { padding: 4px 8px; border-bottom: 1px solid #e3e9ee; }
      table.cuotas th.num { text-align: right; }
      .nota { margin-top: 24px; padding: 10px 14px; border: 1px solid #d7dee5; border-radius: 6px;
              font-size: 10.5px; color: #4a5866; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(c)}
          <div>
            <h1>${esc(c.nombre)}</h1>
            ${c.direccion ? `<p>${esc(c.direccion)}</p>` : ""}
            <p>${[c.rnc ? `RNC: ${esc(c.rnc)}` : "", c.telefono ? `Tel: ${esc(c.telefono)}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Cotización x Mayor</div>
          <p>${esc(q.tipofactura || "Contado")}</p>
          <p># Cotización: <b>${esc(q.id)}</b></p>
          <p>Fecha: <b>${esc(q.fecha)}</b></p>
          ${q.fechavence ? `<p class="vence">Válida hasta: ${esc(q.fechavence)}</p>` : ""}
        </div>
      </div>
      <div class="cliente">
        <div><span>Cliente</span><b>${esc(q.cliente)}</b></div>
        ${q.rnc ? `<div><span>RNC</span><b>${esc(q.rnc)}</b></div>` : ""}
        ${q.telefono ? `<div><span>Teléfono</span><b>${esc(q.telefono)}</b></div>` : ""}
        ${q.vendedor ? `<div><span>Vendedor</span><b>${esc(q.vendedor)}</b></div>` : ""}
        ${
          q.moneda === "U" && Number(q.tasa)
            ? `<div><span>Tasa</span><b>${fmtMoney(q.tasa)}</b></div>`
            : ""
        }
      </div>
      ${
        q.comentario
          ? `<div class="cliente"><div><span>Comentario</span><b>${esc(
              q.comentario
            )}</b></div></div>`
          : ""
      }
      <table class="lineas">
        <tr>
          <th style="width:28px">#</th>
          <th class="num" style="width:60px">Cant.</th>
          <th>Descripción</th>
          <th>Chasis</th>
          <th class="num">Precio Unit. (${mon})</th>
          <th class="num">Importe (${mon})</th>
        </tr>
        ${filasLineas}
      </table>
      <div class="total"><span class="monto">TOTAL: ${mon} ${fmtMoney(q.total)}</span></div>
      ${
        cuotas.length
          ? `<div class="cuotas-titulo">Plan de cuotas (simulado)</div>
      <table class="cuotas">
        <tr><th style="width:50px">#</th><th>Fecha</th><th class="num">Valor (${mon})</th></tr>
        ${filasCuotas}
      </table>`
          : ""
      }
      <div class="nota">
        Esta cotización es solo informativa: no constituye compromiso de venta ni reserva de los
        vehículos. Precios sujetos a cambio sin previo aviso.${
          q.fechavence ? ` Válida hasta el ${esc(q.fechavence)}.` : ""
        }
      </div>
      <div class="firmas">
        <div>Preparado por</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

export default imprimirCotizacionXmayor;
